import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FolderPlus, X } from "lucide-react";
import "./ConfirmDialog.css";

export default function NewProjectModal({
  isOpen,
  onClose,
  onCreate,
  existingProjects = [],
  darkMode = false,
}) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setName("");
      setError("");
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Project name is required");
      return;
    }

    // Check for duplicates (case-insensitive)
    const isDuplicate = existingProjects.some(
      (p) => p.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (isDuplicate) {
      setError("A project with this name already exists");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onCreate(trimmed);
      onClose();
    } catch (err) {
      console.log(err);
      setError(
        err.response?.data?.message || err.message || "Failed to create project",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="confirm-dialog-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        className="confirm-dialog"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          margin: 0,
          minWidth: "320px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-dialog-header">
          <div className="confirm-dialog-icon-wrapper">
            <FolderPlus
              size={24}
              className="confirm-dialog-icon confirm-dialog-icon-warning"
            />
          </div>
          <button
            type="button"
            className="confirm-dialog-close"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>
        <div className="confirm-dialog-content">
          <h3 className="confirm-dialog-title">New Project</h3>
          <p
            className="confirm-dialog-message"
            style={{ marginBottom: "1rem" }}
          >
            Enter a name for your project. Chats can be grouped under this
            project.
          </p>
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              placeholder="Project name"
              autoFocus
              disabled={submitting}
              style={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                fontSize: "1rem",
                border: `1px solid ${darkMode ? "#475569" : "#e2e8f0"}`,
                borderRadius: "0.5rem",
                backgroundColor: darkMode ? "#1e293b" : "#fff",
                color: darkMode ? "#f1f5f9" : "#0f172a",
              }}
            />
            {error && (
              <p
                style={{
                  color: "#ef4444",
                  fontSize: "0.875rem",
                  marginTop: "0.5rem",
                }}
              >
                {error}
              </p>
            )}
          </form>
        </div>
        <div className="confirm-dialog-actions">
          <button
            type="button"
            className="confirm-dialog-button confirm-dialog-button-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="confirm-dialog-button confirm-dialog-button-warning"
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
          >
            {submitting ? "Creating…" : "Create Project"}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
